import { useState, useEffect } from 'react';
import { RefreshCw, TrendingUp, Users, BarChart3, MapPin } from 'lucide-react';
import apiServices from '../../services/api';
import type { ZoneManagerStats } from '../../types/api';

import { ScoreTrendChart } from '../charts/ScoreTrendChart';
import { TopStoresChart } from '../charts/TopStoresChart';
interface ZoneManagerDashboardProps {
  zoneId: string;
}

export const ZoneManagerDashboard: React.FC<ZoneManagerDashboardProps> = ({
  zoneId,
}) => {
  const [stats, setStats] = useState<ZoneManagerStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadStats = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiServices.admin.getZoneManagerStats(zoneId);
      setStats(data);
    } catch (err) {
      console.error('Error loading zone manager stats:', err);
      setError('Error al cargar estadísticas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (zoneId) {
      loadStats();
    }
  }, [zoneId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (error || !stats) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-lg">
        {error || 'No se pudieron cargar las estadísticas'}
      </div>
    );
  }

  const { overview, scoreTrend, representativePerformance, topStores } = stats;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
            Dashboard Zone Manager
          </h2>
          <p className="text-sm sm:text-base text-gray-600 mt-1">
            Rendimiento de tu zona
          </p>
        </div>
        <button
          onClick={loadStats}
          disabled={loading}
          className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-3 sm:py-2 bg-primary-dark text-white rounded-lg hover:bg-primary-accent transition-colors disabled:opacity-50 text-sm sm:text-base"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Actualizar
        </button>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <div className="bg-white p-4 sm:p-6 rounded-lg border border-gray-200">
          <BarChart3 className="w-6 h-6 sm:w-8 sm:h-8 text-purple-600 mb-2" />
          <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">
            {overview.totalReports}
          </div>
          <div className="text-xs sm:text-sm text-gray-600">Total Reportes</div>
        </div>

        <div className="bg-white p-4 sm:p-6 rounded-lg border border-gray-200">
          <TrendingUp className="w-6 h-6 sm:w-8 sm:h-8 text-green-600 mb-2" />
          <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">
            {overview.avgScore.toFixed(1)}%
          </div>
          <div className="text-xs sm:text-sm text-gray-600">Promedio Zona</div>
        </div>

        <div className="bg-white p-4 sm:p-6 rounded-lg border border-gray-200">
          <Users className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600 mb-2" />
          <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">
            {overview.reportsThisMonth}
          </div>
          <div className="text-xs sm:text-sm text-gray-600">Este Mes</div>
        </div>

        <div className="bg-white p-4 sm:p-6 rounded-lg border border-gray-200">
          <MapPin className="w-6 h-6 sm:w-8 sm:h-8 text-orange-600 mb-2" />
          <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">
            {overview.representativesCount}
          </div>
          <div className="text-xs sm:text-sm text-gray-600">Representantes</div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        <ScoreTrendChart data={scoreTrend} />

        {/* Representative Performance */}
        <div className="bg-white p-4 sm:p-6 rounded-lg border border-gray-200">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">
            Rendimiento por Representante
          </h3>
          <div className="space-y-3">
            {representativePerformance
              .sort((a, b) => b.avgScore - a.avgScore)
              .map((rep, index) => (
                <div
                  key={rep.representativeId}
                  className="flex items-center gap-3"
                >
                  <div className="w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center bg-purple-100 rounded-full text-xs sm:text-sm font-medium text-purple-700">
                    {index + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <div className="font-medium text-gray-900 text-xs sm:text-sm truncate">
                        {rep.representativeName}
                      </div>
                      <div className="text-base sm:text-lg font-bold text-purple-600 flex-shrink-0 ml-2">
                        {rep.avgScore.toFixed(1)}%
                      </div>
                    </div>
                    <div className="text-[10px] sm:text-xs text-gray-500 mb-1">
                      {rep.totalReports} reportes
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${rep.avgScore}%` }}
                      />
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>

      {/* Top Stores */}
      <TopStoresChart data={topStores} title="Top Puntos de Venta en tu Zona" />
    </div>
  );
};
